import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';
import { useUser } from '../contexts/UserContext';
import { db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import StakingABI from '../abis/XCoinStaking.json';
import XCoinABI from '../abis/XCoinSeriesA.json';

const STAKING_ADDRESS = "0x03A6bb992c962176A8C5E2963a7aC288Cc5AD7CD"; // Verified Staking Address (Mainnet)
const XCOIN_ADDRESS = "0x6508AEB49733f5dAAD56621Bc2AC05193F26b53a";

export const useStaking = () => {
    const { wallet, signer } = useWeb3();
    const { user, userData } = useUser(); // Get Firestore User for logging & Verification
    const [stakingContract, setStakingContract] = useState(null);
    const [xcoinContract, setXcoinContract] = useState(null);

    // User Data
    const [flexibleBalance, setFlexibleBalance] = useState('0');
    const [pendingRewards, setPendingRewards] = useState('0');
    const [lockedDeposits, setLockedDeposits] = useState([]);
    const [walletBalance, setWalletBalance] = useState('0'); // Available XCO

    // UI State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Initialize Contracts
    useEffect(() => {
        if (signer) {
            try {
                const staking = new ethers.Contract(STAKING_ADDRESS, StakingABI.abi, signer);
                const xcoin = new ethers.Contract(XCOIN_ADDRESS, XCoinABI.abi, signer);
                setStakingContract(staking);
                setXcoinContract(xcoin);
                console.log("Staking Contracts Initialized");
            } catch (e) {
                console.error("Error initializing contracts:", e);
            }
        }
    }, [signer]);

    const fetchData = useCallback(async () => {
        if (!stakingContract || !xcoinContract || !wallet) return;

        try {
            const userAddr = wallet.accounts[0].address;

            // 0. Wallet Balance (Available XCO)
            const bal = await xcoinContract.balanceOf(userAddr);
            setWalletBalance(ethers.formatUnits(bal, 18));

            // 1. Flexible Data
            const flexStaker = await stakingContract.flexibleStakers(userAddr);
            setFlexibleBalance(ethers.formatUnits(flexStaker.amount, 18));

            const earned = await stakingContract.pendingRewardFlexible(userAddr);
            setPendingRewards(ethers.formatUnits(earned, 18));

            // 2. Locked Data
            const deposits = await stakingContract.getLockedDeposits(userAddr);
            // Transform Struct to Object
            const formattedDeposits = deposits.map(d => ({
                id: d.id.toString(),
                amount: ethers.formatUnits(d.amount, 18),
                period: Number(d.period), // Enum: 0=30d, 1=90d, 2=180d
                apy: Number(d.apy) / 100, // Basis Points 3000 -> 30%
                startTime: Number(d.startTime),
                unlockTime: Number(d.unlockTime),
                withdrawn: d.withdrawn
            }));
            setLockedDeposits(formattedDeposits);

        } catch (err) {
            console.error("Error fetching staking data:", err);
        }
    }, [stakingContract, xcoinContract, wallet, userData]);

    // Refresh data periodically
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [fetchData]);

    // --- HELPER: LOG TRANSACTION ---
    const logTransaction = async (type, amount, txHash) => {
        if (!user) return;
        try {
            const estimatedPrice = 9000;
            const API_URL = import.meta.env.VITE_API_URL || 'https://dashboard.xcoin.cl';

            await fetch(`${API_URL}/api/transaccion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.uid,
                    tipo: type,
                    moneda: 'XCO',
                    cantidad: parseFloat(amount),
                    montoCLP: parseFloat(amount) * estimatedPrice,
                    txHash: txHash,
                    method: 'CLP',
                    status: 'confirmed',
                    origen: 'Staking'
                })
            });
        } catch (e) {
            console.error("Error logging transaction to PostgreSQL:", e);
        }
    };

    // --- ACTIONS ---

    const stakeFlexible = async (amountXCO) => {
        setLoading(true); setError(''); setSuccessMsg('');

        if (!xcoinContract || !stakingContract) {
            console.error("Contracts not initialized yet.");
            setError("Error de conexión: Contratos no cargados. Por favor recarga la página.");
            setLoading(false);
            return;
        }

        try {
            const amountWei = ethers.parseUnits(amountXCO.toString(), 18);

            // 1. Approve
            console.log("Checking allowance for:", wallet.accounts[0].address);
            const allowance = await xcoinContract.allowance(wallet.accounts[0].address, STAKING_ADDRESS);

            if (allowance < amountWei) {
                console.log("Approving...");
                const txApprove = await xcoinContract.approve(STAKING_ADDRESS, amountWei);
                await txApprove.wait();
                console.log("Approved.");
            }

            // 2. Deposit
            const tx = await stakingContract.depositFlexible(amountWei);
            const receipt = await tx.wait();

            await logTransaction("Depósito Staking Flexible", amountXCO, receipt.hash);

            setSuccessMsg(`Depositado exitosamente: ${amountXCO} XCO`);
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.reason || err.message || "Error desconocido");
        } finally {
            setLoading(false);
        }
    };

    const withdrawFlexible = async (amountXCO) => {
        setLoading(true); setError(''); setSuccessMsg('');

        if (!stakingContract) {
            setError("Contrato no disponible");
            setLoading(false);
            return;
        }

        try {
            const amountWei = ethers.parseUnits(amountXCO.toString(), 18);
            const tx = await stakingContract.withdrawFlexible(amountWei);
            const receipt = await tx.wait();

            await logTransaction("Retiro Staking Flexible", amountXCO, receipt.hash);

            setSuccessMsg(`Retirado exitosamente: ${amountXCO} XCO`);
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.reason || err.message);
        } finally {
            setLoading(false);
        }
    };

    const claimFlexible = async () => {
        setLoading(true); setError(''); setSuccessMsg('');
        if (!stakingContract) return;

        try {
            const tx = await stakingContract.claimFlexibleRewards();
            const receipt = await tx.wait();

            await logTransaction("Reclamo Recompensas Flexible", pendingRewards, receipt.hash);

            setSuccessMsg("¡Recompensas reclamadas!");
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.reason || err.message);
        } finally {
            setLoading(false);
        }
    };

    // Period: 0 (30d), 1 (90d), 2 (180d)
    const stakeLocked = async (amountXCO, periodEnum) => {
        setLoading(true); setError(''); setSuccessMsg('');

        if (!xcoinContract || !stakingContract) {
            setError("Error de conexión: Contratos no cargados.");
            setLoading(false);
            return;
        }

        try {
            const amountWei = ethers.parseUnits(amountXCO.toString(), 18);

            // 1. Approve
            const allowance = await xcoinContract.allowance(wallet.accounts[0].address, STAKING_ADDRESS);
            if (allowance < amountWei) {
                const txApprove = await xcoinContract.approve(STAKING_ADDRESS, amountWei);
                await txApprove.wait();
            }

            // 2. Deposit
            const tx = await stakingContract.depositLocked(amountWei, periodEnum);
            const receipt = await tx.wait();

            await logTransaction(`Depósito Plazo Fijo (${periodEnum === 0 ? '30d' : periodEnum === 1 ? '90d' : '180d'})`, amountXCO, receipt.hash);

            setSuccessMsg("¡Depósito a Plazo creado exitosamente!");
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.reason || err.message);
        } finally {
            setLoading(false);
        }
    };

    const withdrawLocked = async (depositIndex) => {
        setLoading(true); setError(''); setSuccessMsg('');
        if (!stakingContract) return;

        try {
            // Need amount for log? We only have index.
            // We can look it up in 'lockedDeposits' state if index matches
            // but let's just log "Retiro Plazo Fijo" generic or try to find it.
            const deposit = lockedDeposits[depositIndex];
            const amount = deposit ? deposit.amount : "Unknown";

            const tx = await stakingContract.withdrawLocked(depositIndex);
            const receipt = await tx.wait();

            await logTransaction("Retiro Plazo Fijo", amount, receipt.hash);

            setSuccessMsg("¡Depósito y ganancias retirados!");
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.reason || err.message);
        } finally {
            setLoading(false);
        }
    };

    return {
        // Data
        flexibleBalance,
        pendingRewards,
        lockedDeposits,
        walletBalance,

        // Actions
        stakeFlexible,
        withdrawFlexible,
        claimFlexible,
        stakeLocked,
        withdrawLocked,

        // UI
        loading,
        error,
        successMsg
    };
};
